import { useCallback, useEffect } from 'react';
import { FileTypeEnum } from 'api/documents';
import {
  useDocumentNotifications,
  DocumentProgressDto
} from 'shared/signalR/hooks/useDocumentNotifications';
import { toasterService } from 'components/feedback/Toaster';
import {
  useBorrowerGlobalInfos,
  useBorrowerContractualizationActions
} from '../store';
import { useProcessedNotifications } from './useProcessedNotifications';

interface UseBorrowerDocumentsSyncOptions {
  /** Called when documents are updated (for external refresh) */
  onDocumentsUpdated?: () => void;
  /** Show toast notifications (default: true) */
  showToasts?: boolean;
}

/**
 * Hook centralisant la synchronisation des documents emprunteur :
 * - Connexion SignalR pour les notifications temps réel
 * - Fetch initial des documents via fetchBorrowerDocuments
 * - Refresh automatique sur notification
 * - Affichage des toasts de notification
 *
 * @example
 * ```tsx
 * const { isConnected, refresh } = useBorrowerDocumentsSync();
 * ```
 */
export const useBorrowerDocumentsSync = (
  options: UseBorrowerDocumentsSyncOptions = {}
) => {
  const { onDocumentsUpdated, showToasts = true } = options;
  const globalInfos = useBorrowerGlobalInfos();
  const { fetchBorrowerDocuments } = useBorrowerContractualizationActions();

  const { memoizedConnect, notifications, isConnected } =
    useDocumentNotifications(globalInfos?.quoteId);

  // Connexion SignalR : useSignalR gère lui-meme la deconnexion au unmount,
  // ce qui garantit que LeaveDevisGroup est invoque AVANT la fermeture de la connexion.
  useEffect(() => {
    memoizedConnect();
  }, [memoizedConnect]);

  // Fetch initial des documents
  useEffect(() => {
    if (globalInfos?.quoteId) {
      fetchBorrowerDocuments(globalInfos.quoteId);
    }
  }, [fetchBorrowerDocuments, globalInfos?.quoteId]);

  // Handler pour les nouvelles notifications
  const handleNewNotification = useCallback(
    (notification: DocumentProgressDto) => {
      // Afficher le toast si configuré
      if (showToasts && notification.notificationPopIn) {
        toasterService.showToast({
          title: notification.notificationPopIn.title,
          subtitle: notification.notificationPopIn.subtitle,
          message: notification.notificationPopIn.message,
          displayType: notification.notificationPopIn.displayType,
          color: notification.notificationPopIn.color
        });
      }

      // Rafraîchir les documents
      if (globalInfos?.quoteId) {
        fetchBorrowerDocuments(globalInfos.quoteId);
      }

      // Appel du callback si certificat d'adhésion reçu
      if (notification.fileType === FileTypeEnum.CertificatAdhesion) {
        onDocumentsUpdated?.();
      }
    },
    [fetchBorrowerDocuments, globalInfos?.quoteId, onDocumentsUpdated, showToasts]
  );

  // Utilisation du hook centralisé pour la déduplication
  useProcessedNotifications(notifications, handleNewNotification);

  return {
    /** Whether SignalR is connected */
    isConnected,
    /** Manually refresh documents */
    refresh: useCallback(() => {
      if (globalInfos?.quoteId) {
        fetchBorrowerDocuments(globalInfos.quoteId);
      }
    }, [fetchBorrowerDocuments, globalInfos?.quoteId])
  };
};
